// @ts-check

class DoogBlogFilters extends HTMLElement {
  constructor() {
    super();

    /** @type {string} */
    this.gridSelector = '';

    /** @type {string} */
    this.loadMoreSlotSelector = '';

    /** @type {boolean} */
    this.isLoading = false;

    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    const gridSelector = this.dataset.gridSelector;
    const loadMoreSlotSelector = this.dataset.loadMoreSlotSelector;

    if (!gridSelector || !loadMoreSlotSelector) {
      return;
    }

    this.gridSelector = gridSelector;
    this.loadMoreSlotSelector = loadMoreSlotSelector;

    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  /**
   * @param {MouseEvent} event
   */
  async handleClick(event) {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const filterLink = target.closest('[data-doog-blog-filter]');

    if (!(filterLink instanceof HTMLAnchorElement)) {
      return;
    }

    event.preventDefault();

    if (this.isLoading) {
      return;
    }

    await this.filterByUrl(filterLink.href, filterLink);
  }

  /**
   * @param {string} url
   * @param {HTMLAnchorElement} activeLink
   */
  async filterByUrl(url, activeLink) {
    this.isLoading = true;
    this.setActiveLoading(activeLink, true);

    try {
      const requestUrl = new URL(url, window.location.href).toString();

      const response = await fetch(requestUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Error al filtrar artículos. Código: ${response.status}`);
      }

      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');

      this.replaceGrid(nextDocument);
      this.replaceLoadMoreSlot(nextDocument);
      this.updateActiveFilter(activeLink);
    } catch (error) {
      console.error(error);
    } finally {
      this.setActiveLoading(activeLink, false);
      this.isLoading = false;
    }
  }

  /**
   * @param {Document} nextDocument
   */
  replaceGrid(nextDocument) {
    const currentGrid = document.querySelector(this.gridSelector);
    const nextGrid = nextDocument.querySelector(this.gridSelector);

    if (!(currentGrid instanceof HTMLElement)) {
      throw new Error(`No se ha encontrado el grid actual: ${this.gridSelector}`);
    }

    if (!(nextGrid instanceof HTMLElement)) {
      throw new Error(`No se ha encontrado el grid filtrado: ${this.gridSelector}`);
    }

    currentGrid.replaceChildren();

    Array.from(nextGrid.children).forEach((child) => {
      const clonedChild = child.cloneNode(true);

      if (clonedChild instanceof HTMLElement) {
        currentGrid.appendChild(clonedChild);
      }
    });
  }

  /**
   * @param {Document} nextDocument
   */
  replaceLoadMoreSlot(nextDocument) {
    const currentSlot = document.querySelector(this.loadMoreSlotSelector);
    const nextSlot = nextDocument.querySelector(this.loadMoreSlotSelector);

    if (!(currentSlot instanceof HTMLElement)) {
      return;
    }

    if (!(nextSlot instanceof HTMLElement)) {
      currentSlot.replaceChildren();
      return;
    }

    currentSlot.innerHTML = nextSlot.innerHTML;
  }

  /**
   * @param {HTMLAnchorElement} activeLink
   */
  updateActiveFilter(activeLink) {
    const filterLinks = this.querySelectorAll('[data-doog-blog-filter]');

    filterLinks.forEach((link) => {
      link.classList.remove('is-active');
      link.removeAttribute('aria-current');
    });

    activeLink.classList.add('is-active');
    activeLink.setAttribute('aria-current', 'page');
  }

  /**
   * @param {HTMLAnchorElement} link
   * @param {boolean} isLoading
   */
  setActiveLoading(link, isLoading) {
    link.setAttribute('aria-busy', String(isLoading));
    link.classList.toggle('is-loading', isLoading);
  }
}

if (!customElements.get('doog-blog-filters')) {
  customElements.define('doog-blog-filters', DoogBlogFilters);
}